import { createEffect, onCleanup } from "solid-js";

export interface AirbrxAgentAction {
  id: string;
  label: string;
  kind?: "navigate" | "submit" | "review" | "refresh" | "toggle" | "other";
  description?: string;
  href?: string;
  testId?: string;
}

export interface AirbrxAgentFeatureHighlight {
  id: string;
  label: string;
  kind?: "feature" | "metric" | "config" | "navigation" | "validation";
  successSignal?: string;
  critical?: boolean;
  minimumDwellMs?: number;
}

export interface AirbrxAgentRouteDefinition {
  id: string;
  title: string;
  examplePath: string;
  matcher: RegExp;
  navLabel?: string;
  summary?: string;
  primaryActions?: AirbrxAgentAction[];
}

export interface AirbrxAgentVisibleControl {
  kind: string;
  label?: string;
  testId?: string;
  selectorHint: string;
  role?: string;
  name?: string;
  disabled: boolean;
  href?: string;
}

export interface AirbrxAgentInteraction {
  timestamp: string;
  atMs: number;
  type: "hover" | "click" | "focus" | "blur" | "change" | "submit";
  app: string;
  path: string;
  routeId?: string;
  pageId?: string;
  label?: string;
  target: string;
  testId?: string;
  role?: string;
  valuePreview?: string;
}

export interface AirbrxAgentPageSnapshot {
  pageId: string;
  title: string;
  path?: string;
  summary?: string;
  primaryActions?: AirbrxAgentAction[];
  featureHighlights?: AirbrxAgentFeatureHighlight[];
  minimumCaptureMs?: number;
  visibleState?: Record<string, unknown>;
  diagnostics?: Record<string, unknown>;
  captureReady?: boolean;
  captureBlockers?: string[];
  qualityNotes?: string[];
}

export interface AirbrxAgentSerializableRoute {
  id: string;
  title: string;
  examplePath: string;
  navLabel?: string;
  summary?: string;
  primaryActions?: AirbrxAgentAction[];
  matcher: string;
}

export interface AirbrxAgentRecordedRoute {
  timestamp: string;
  atMs: number;
  route: {
    id: string;
    title: string;
    path: string;
    examplePath: string;
    navLabel?: string;
    summary?: string;
    primaryActions?: AirbrxAgentAction[];
  };
}

export interface AirbrxAgentRecordedPage {
  timestamp: string;
  atMs: number;
  page: AirbrxAgentPageSnapshot;
}

export interface AirbrxAgentPerformanceSnapshot {
  navigationType?: string;
  domContentLoadedMs?: number;
  loadEventMs?: number;
  firstContentfulPaintMs?: number;
  longTaskCount?: number;
  totalLongTaskMs?: number;
  maxLongTaskMs?: number;
  interactionEventCount?: number;
  slowInteractionCount?: number;
  worstInteractionMs?: number;
  layoutShiftCount?: number;
  cumulativeLayoutShift?: number;
  maxLayoutShift?: number;
}

export interface AirbrxAgentReadinessSnapshot {
  timestamp: string;
  atMs: number;
  routeId?: string;
  pageId?: string;
  path: string;
  busy: boolean;
  busyIndicatorCount: number;
  blockingDialogCount: number;
  alertCount: number;
  errorCount: number;
  emptyStateCount: number;
  statusCount: number;
  visibleControlCount: number;
  headingTexts: string[];
  alertTexts: string[];
  emptyStateTexts: string[];
  primaryActionLabels: string[];
  declaredBusy: boolean;
  declaredErrorCount: number;
  captureReady: boolean;
  blockers: string[];
  routeActiveMs?: number;
  pageActiveMs?: number;
  performance: AirbrxAgentPerformanceSnapshot;
}

export interface AirbrxAgentTimelineEvent {
  timestamp: string;
  atMs: number;
  kind: "route" | "page" | "page-cleared" | "interaction" | "readiness";
  app: string;
  path: string;
  routeId?: string;
  pageId?: string;
  detail?: string;
  target?: string;
  interactionType?: AirbrxAgentInteraction["type"];
}

export interface AirbrxAgentStateSnapshot {
  app: string;
  initializedAt: string;
  route?: {
    id: string;
    title: string;
    path: string;
    examplePath: string;
    navLabel?: string;
    summary?: string;
    primaryActions?: AirbrxAgentAction[];
  };
  page?: AirbrxAgentPageSnapshot;
  readiness?: AirbrxAgentReadinessSnapshot;
  routes: AirbrxAgentSerializableRoute[];
  interactions: AirbrxAgentInteraction[];
  routeHistory: AirbrxAgentRecordedRoute[];
  pageHistory: AirbrxAgentRecordedPage[];
  readinessHistory: AirbrxAgentReadinessSnapshot[];
  timeline: AirbrxAgentTimelineEvent[];
}

export interface AirbrxAgentApi {
  getState(): AirbrxAgentStateSnapshot;
  getPage(): AirbrxAgentPageSnapshot | undefined;
  getReadiness(): AirbrxAgentReadinessSnapshot | undefined;
  getTimeline(): AirbrxAgentTimelineEvent[];
  listRoutes(): AirbrxAgentSerializableRoute[];
  listVisibleControls(): AirbrxAgentVisibleControl[];
  getInteractions(): AirbrxAgentInteraction[];
  clearInteractions(): void;
  updateRoute(path: string): void;
  updatePage(page: AirbrxAgentPageSnapshot): void;
  clearPage(pageId?: string): void;
  describeCurrentPage(): {
    route?: AirbrxAgentStateSnapshot["route"];
    page?: AirbrxAgentPageSnapshot;
    readiness?: AirbrxAgentReadinessSnapshot;
    controls: AirbrxAgentVisibleControl[];
  };
  destroy(): void;
}

declare global {
  interface Window {
    __AIRBRX_AGENT__?: AirbrxAgentApi;
    airbrxAgent?: AirbrxAgentApi;
  }
}

interface AirbrxAgentRuntimeConfig {
  app: string;
  routes: AirbrxAgentRouteDefinition[];
  interactionBufferSize?: number;
  timelineBufferSize?: number;
  readinessBufferSize?: number;
}

interface RouteState {
  id: string;
  title: string;
  path: string;
  examplePath: string;
  navLabel?: string;
  summary?: string;
  primaryActions?: AirbrxAgentAction[];
}

interface AirbrxAgentRuntimePerformanceState {
  longTaskCount: number;
  totalLongTaskMs: number;
  maxLongTaskMs: number;
  interactionEventCount: number;
  slowInteractionCount: number;
  worstInteractionMs: number;
  layoutShiftCount: number;
  cumulativeLayoutShift: number;
  maxLayoutShift: number;
}

const DEFAULT_INTERACTION_BUFFER_SIZE = 400;
const DEFAULT_TIMELINE_BUFFER_SIZE = 600;
const DEFAULT_READINESS_BUFFER_SIZE = 180;
const INTERACTIVE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  "[role='button']",
  "[role='link']",
  "[role='tab']",
  "[role='checkbox']",
  "[role='radio']",
  "[role='switch']",
  "[data-testid]",
].join(", ");
const BUSY_SELECTOR = [
  "[aria-busy='true']",
  "[role='progressbar']",
  ".loading-spinner",
  ".loading-spinner-sm",
  ".loading-spinner-lg",
  ".skeleton",
].join(", ");
const ALERT_SELECTOR = [
  "[role='alert']",
  ".alert-error",
  ".error-banner",
  ".error-state",
  ".toast-error",
  ".cver-error-card",
].join(", ");
const EMPTY_STATE_SELECTOR = [
  ".empty-state",
  ".cver-empty",
  "[data-empty-state='true']",
  "[data-testid*='empty']",
].join(", ");
const STATUS_SELECTOR = "[role='status']";
const BLOCKING_DIALOG_SELECTOR = "dialog[open], [role='dialog'][aria-modal='true']";
const PRIMARY_ACTION_SELECTOR = ".btn-cf-primary, .btn-primary, [data-airbrx-primary-action='true']";
const ERROR_TEXT_RE = /(error|failed|failure|something went wrong|invalid|mismatch|denied|forbidden|unable)/i;
const DECLARED_BUSY_KEY_RE = /(loading|pending|submitting|creating|executing|refreshing|validating|testing|running|saving|syncing|fetching|working)/i;
const DECLARED_ERROR_KEY_RE = /(error|errors|failed|failure|invalid|warning|warnings)/i;

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function markUserTiming(name: string): void {
  if (typeof performance === "undefined" || typeof performance.mark !== "function") return;
  try {
    performance.mark(name);
  } catch {
    // Ignore unsupported mark names or runtimes.
  }
}

function pushLimited<T>(list: T[], next: T, limit: number): void {
  list.push(next);
  while (list.length > limit) list.shift();
}

export function matchAirbrxRoute(
  pathname: string,
  routes: AirbrxAgentRouteDefinition[],
): AirbrxAgentRouteDefinition | undefined {
  return routes.find((route) => route.matcher.test(pathname));
}

function sanitizeValuePreview(element: HTMLElement): string | undefined {
  if (element instanceof HTMLInputElement) {
    if (element.type === "password") return "[redacted]";
    if (element.type === "checkbox" || element.type === "radio") return String(element.checked);
    return element.value ? element.value.slice(0, 120) : undefined;
  }
  if (element instanceof HTMLSelectElement) {
    return element.value || undefined;
  }
  if (element instanceof HTMLTextAreaElement) {
    return element.value ? element.value.slice(0, 120) : undefined;
  }
  return undefined;
}

function isElementVisible(element: HTMLElement): boolean {
  if (element.hidden || element.getAttribute("aria-hidden") === "true") return false;
  if (element instanceof HTMLInputElement && element.type === "hidden") return false;
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") return false;
  return true;
}

function findLabelText(element: HTMLElement): string | undefined {
  const ariaLabel = element.getAttribute("aria-label")?.trim();
  if (ariaLabel) return ariaLabel;

  const labelledBy = element.getAttribute("aria-labelledby");
  if (labelledBy) {
    const texts = labelledBy
      .split(/\s+/)
      .map((id) => document.getElementById(id)?.textContent?.trim())
      .filter((text): text is string => Boolean(text));
    if (texts.length > 0) return texts.join(" ");
  }

  if (element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement) {
    if (element.labels && element.labels.length > 0) {
      const labelText = Array.from(element.labels)
        .map((label) => label.textContent?.trim())
        .filter((text): text is string => Boolean(text))
        .join(" ");
      if (labelText) return labelText;
    }
  }

  const closestLabel = element.closest("label");
  const closestLabelText = closestLabel?.textContent?.trim();
  if (closestLabelText) return closestLabelText;

  const text = element.textContent?.trim();
  if (text) return text.replace(/\s+/g, " ").slice(0, 120);

  const placeholder = element.getAttribute("placeholder")?.trim();
  if (placeholder) return placeholder;

  const name = element.getAttribute("name")?.trim();
  if (name) return name;

  const id = element.getAttribute("id")?.trim();
  if (id) return id;

  return undefined;
}

function selectorHintForElement(element: HTMLElement): string {
  const testId = element.getAttribute("data-testid");
  if (testId) return `[data-testid="${testId}"]`;
  if (element.id) return `#${element.id}`;
  const name = element.getAttribute("name");
  if (name) return `${element.tagName.toLowerCase()}[name="${name}"]`;
  const ariaLabel = element.getAttribute("aria-label");
  if (ariaLabel) return `${element.tagName.toLowerCase()}[aria-label="${ariaLabel}"]`;
  return element.tagName.toLowerCase();
}

function resolveInteractiveElement(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  const direct = target.closest<HTMLElement>(INTERACTIVE_SELECTOR);
  return direct ?? (target instanceof HTMLElement ? target : null);
}

function buildControlDescriptor(element: HTMLElement): AirbrxAgentVisibleControl {
  const href = element instanceof HTMLAnchorElement ? element.href : undefined;
  const role = element.getAttribute("role") || undefined;
  const name = element.getAttribute("name") || undefined;
  const kind = element.tagName.toLowerCase();

  return {
    kind,
    label: findLabelText(element),
    testId: element.getAttribute("data-testid") || undefined,
    selectorHint: selectorHintForElement(element),
    role,
    name,
    disabled:
      element.hasAttribute("disabled") ||
      element.getAttribute("aria-disabled") === "true" ||
      (element instanceof HTMLInputElement && element.disabled) ||
      (element instanceof HTMLButtonElement && element.disabled) ||
      (element instanceof HTMLSelectElement && element.disabled) ||
      (element instanceof HTMLTextAreaElement && element.disabled),
    href,
  };
}

function listVisibleControlsFromDocument(): AirbrxAgentVisibleControl[] {
  if (typeof document === "undefined") return [];
  const seen = new Set<string>();
  const controls: AirbrxAgentVisibleControl[] = [];
  const elements = Array.from(document.querySelectorAll<HTMLElement>(INTERACTIVE_SELECTOR));

  for (const element of elements) {
    if (!isElementVisible(element)) continue;
    const descriptor = buildControlDescriptor(element);
    const key = `${descriptor.selectorHint}::${descriptor.label || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    controls.push(descriptor);
  }

  return controls;
}

function visibleTextList(selector: string, limit = 6): string[] {
  if (typeof document === "undefined") return [];
  return Array.from(document.querySelectorAll<HTMLElement>(selector))
    .filter((element) => isElementVisible(element))
    .map((element) => element.textContent?.replace(/\s+/g, " ").trim() || "")
    .filter(Boolean)
    .slice(0, limit);
}

function humanizeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function collectDeclaredErrorMessages(value: unknown, messages: string[], depth = 0): void {
  if (depth > 2 || messages.length >= 6 || value === null || value === undefined) return;
  if (typeof value === "string") {
    const normalized = value.replace(/\s+/g, " ").trim();
    if (normalized) messages.push(normalized.slice(0, 180));
    return;
  }
  if (typeof value === "number") {
    return;
  }
  if (typeof value === "boolean") {
    if (value) messages.push("present");
    return;
  }
  if (Array.isArray(value)) {
    for (const entry of value) collectDeclaredErrorMessages(entry, messages, depth + 1);
    return;
  }
  if (typeof value === "object") {
    for (const entry of Object.values(value as Record<string, unknown>)) {
      collectDeclaredErrorMessages(entry, messages, depth + 1);
      if (messages.length >= 6) break;
    }
  }
}

function summarizePageSignals(page: AirbrxAgentPageSnapshot | undefined): {
  declaredBusy: boolean;
  declaredErrorCount: number;
  blockers: string[];
  captureReady: boolean | undefined;
} {
  if (!page) return { declaredBusy: false, declaredErrorCount: 0, blockers: [], captureReady: undefined };

  const blockers = [...(page.captureBlockers || [])];
  let declaredBusy = false;
  let declaredErrorCount = 0;

  for (const [key, value] of Object.entries(page.visibleState || {})) {
    if (value === null || value === undefined) continue;
    if (DECLARED_BUSY_KEY_RE.test(key)) {
      const active =
        value === true ||
        (typeof value === "string" && value.trim().length > 0 && !/false|idle|done|ready|success/i.test(value));
      if (active) {
        declaredBusy = true;
        blockers.push(`${humanizeKey(key)} in progress`);
      }
    }
    if (DECLARED_ERROR_KEY_RE.test(key)) {
      const messages: string[] = [];
      collectDeclaredErrorMessages(value, messages);
      if (messages.length > 0) {
        declaredErrorCount += messages.length;
        blockers.push(...messages.map((message) => `${humanizeKey(key)}: ${message}`));
      } else if (value === true) {
        declaredErrorCount += 1;
        blockers.push(`${humanizeKey(key)} present`);
      }
    }
  }

  return {
    declaredBusy,
    declaredErrorCount,
    blockers: blockers.filter((value, index, list) => Boolean(value) && list.indexOf(value) === index).slice(0, 8),
    captureReady: page.captureReady,
  };
}

function getPerformanceSnapshot(runtime: AirbrxAgentRuntimePerformanceState): AirbrxAgentPerformanceSnapshot {
  if (typeof performance === "undefined") return {};

  const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  const firstContentfulPaint = performance
    .getEntriesByType("paint")
    .find((entry) => entry.name === "first-contentful-paint");

  return {
    navigationType: navigation?.type,
    domContentLoadedMs: navigation ? Math.round(navigation.domContentLoadedEventEnd) : undefined,
    loadEventMs: navigation ? Math.round(navigation.loadEventEnd) : undefined,
    firstContentfulPaintMs: firstContentfulPaint ? Math.round(firstContentfulPaint.startTime) : undefined,
    longTaskCount: runtime.longTaskCount || undefined,
    totalLongTaskMs: runtime.totalLongTaskMs ? Math.round(runtime.totalLongTaskMs) : undefined,
    maxLongTaskMs: runtime.maxLongTaskMs || undefined,
    interactionEventCount: runtime.interactionEventCount || undefined,
    slowInteractionCount: runtime.slowInteractionCount || undefined,
    worstInteractionMs: runtime.worstInteractionMs || undefined,
    layoutShiftCount: runtime.layoutShiftCount || undefined,
    cumulativeLayoutShift: runtime.cumulativeLayoutShift
      ? Number(runtime.cumulativeLayoutShift.toFixed(4))
      : undefined,
    maxLayoutShift: runtime.maxLayoutShift ? Number(runtime.maxLayoutShift.toFixed(4)) : undefined,
  };
}

function buildReadinessSnapshot(
  controls: AirbrxAgentVisibleControl[],
  route: RouteState | undefined,
  page: AirbrxAgentPageSnapshot | undefined,
  routeUpdatedAtMs: number | undefined,
  pageUpdatedAtMs: number | undefined,
  runtimePerformance: AirbrxAgentRuntimePerformanceState,
): AirbrxAgentReadinessSnapshot {
  const now = Date.now();
  const alertTexts = visibleTextList(ALERT_SELECTOR, 8);
  const emptyStateTexts = visibleTextList(EMPTY_STATE_SELECTOR, 6);
  const headingTexts = visibleTextList("h1, h2", 8);
  const primaryActionLabels = [
    ...(page?.primaryActions?.map((action) => action.label) || []),
    ...visibleTextList(PRIMARY_ACTION_SELECTOR, 6),
  ].filter((value, index, list) => Boolean(value) && list.indexOf(value) === index);
  const declaredSignals = summarizePageSignals(page);
  const busyIndicatorCount = Array.from(document.querySelectorAll<HTMLElement>(BUSY_SELECTOR)).filter((element) =>
    isElementVisible(element),
  ).length;
  const blockingDialogCount = Array.from(document.querySelectorAll<HTMLElement>(BLOCKING_DIALOG_SELECTOR)).filter((element) =>
    isElementVisible(element),
  ).length;
  const alertCount = Array.from(document.querySelectorAll<HTMLElement>(ALERT_SELECTOR)).filter((element) =>
    isElementVisible(element),
  ).length;
  const emptyStateCount = Array.from(document.querySelectorAll<HTMLElement>(EMPTY_STATE_SELECTOR)).filter((element) =>
    isElementVisible(element),
  ).length;
  const statusCount = Array.from(document.querySelectorAll<HTMLElement>(STATUS_SELECTOR)).filter((element) =>
    isElementVisible(element),
  ).length;
  const errorCount = alertTexts.filter((text) => ERROR_TEXT_RE.test(text)).length;
  const blockers = [
    ...declaredSignals.blockers,
    ...alertTexts.filter((text) => ERROR_TEXT_RE.test(text)).slice(0, 4),
  ].filter((value, index, list) => Boolean(value) && list.indexOf(value) === index);
  const captureReady =
    typeof declaredSignals.captureReady === "boolean"
      ? declaredSignals.captureReady
      : !declaredSignals.declaredBusy &&
        declaredSignals.declaredErrorCount === 0 &&
        busyIndicatorCount === 0 &&
        blockingDialogCount === 0 &&
        errorCount === 0;

  return {
    timestamp: new Date(now).toISOString(),
    atMs: now,
    routeId: route?.id,
    pageId: page?.pageId,
    path: window.location.pathname,
    busy: busyIndicatorCount > 0 || blockingDialogCount > 0,
    busyIndicatorCount,
    blockingDialogCount,
    alertCount,
    errorCount,
    emptyStateCount,
    statusCount,
    visibleControlCount: controls.length,
    headingTexts,
    alertTexts,
    emptyStateTexts,
    primaryActionLabels,
    declaredBusy: declaredSignals.declaredBusy,
    declaredErrorCount: declaredSignals.declaredErrorCount,
    captureReady,
    blockers,
    routeActiveMs: routeUpdatedAtMs ? Math.max(0, now - routeUpdatedAtMs) : undefined,
    pageActiveMs: pageUpdatedAtMs ? Math.max(0, now - pageUpdatedAtMs) : undefined,
    performance: getPerformanceSnapshot(runtimePerformance),
  };
}

function readinessFingerprint(snapshot: AirbrxAgentReadinessSnapshot): string {
  return JSON.stringify({
    routeId: snapshot.routeId,
    pageId: snapshot.pageId,
    path: snapshot.path,
    busy: snapshot.busy,
    busyIndicatorCount: snapshot.busyIndicatorCount,
    blockingDialogCount: snapshot.blockingDialogCount,
    alertCount: snapshot.alertCount,
    errorCount: snapshot.errorCount,
    emptyStateCount: snapshot.emptyStateCount,
    statusCount: snapshot.statusCount,
    headingTexts: snapshot.headingTexts,
    alertTexts: snapshot.alertTexts,
    emptyStateTexts: snapshot.emptyStateTexts,
    primaryActionLabels: snapshot.primaryActionLabels,
    declaredBusy: snapshot.declaredBusy,
    declaredErrorCount: snapshot.declaredErrorCount,
    captureReady: snapshot.captureReady,
    blockers: snapshot.blockers,
  });
}

function updateDocumentAttributes(
  app: string,
  route?: RouteState,
  page?: AirbrxAgentPageSnapshot,
  readiness?: AirbrxAgentReadinessSnapshot,
): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const body = document.body;
  const attrs: Array<[string, string | undefined]> = [
    ["data-airbrx-app", app],
    ["data-airbrx-route-id", route?.id],
    ["data-airbrx-route-title", route?.title],
    ["data-airbrx-page-id", page?.pageId],
    ["data-airbrx-page-title", page?.title],
    ["data-airbrx-feature-count", page?.featureHighlights ? String(page.featureHighlights.length) : undefined],
    [
      "data-airbrx-critical-feature-count",
      page?.featureHighlights ? String(page.featureHighlights.filter((feature) => feature.critical).length) : undefined,
    ],
    ["data-airbrx-min-capture-ms", page?.minimumCaptureMs ? String(page.minimumCaptureMs) : undefined],
    ["data-airbrx-page-busy", readiness ? String(readiness.busy) : undefined],
    ["data-airbrx-capture-ready", readiness ? String(readiness.captureReady) : undefined],
    ["data-airbrx-alert-count", readiness ? String(readiness.alertCount) : undefined],
    ["data-airbrx-blocker-count", readiness ? String(readiness.blockers.length) : undefined],
    ["data-airbrx-empty-count", readiness ? String(readiness.emptyStateCount) : undefined],
  ];

  for (const [name, value] of attrs) {
    if (value) {
      root.setAttribute(name, value);
      body?.setAttribute(name, value);
    } else {
      root.removeAttribute(name);
      body?.removeAttribute(name);
    }
  }
}

function toSerializableRoute(route: AirbrxAgentRouteDefinition): AirbrxAgentSerializableRoute {
  return {
    id: route.id,
    title: route.title,
    examplePath: route.examplePath,
    navLabel: route.navLabel,
    summary: route.summary,
    primaryActions: route.primaryActions,
    matcher: route.matcher.toString(),
  };
}

export function initializeAirbrxAgent(config: AirbrxAgentRuntimeConfig): AirbrxAgentApi {
  if (typeof window === "undefined" || typeof document === "undefined") {
    const noOpState: AirbrxAgentStateSnapshot = {
      app: config.app,
      initializedAt: new Date().toISOString(),
      routes: config.routes.map(toSerializableRoute),
      interactions: [],
      routeHistory: [],
      pageHistory: [],
      readinessHistory: [],
      timeline: [],
    };
    return {
      getState: () => noOpState,
      getPage: () => undefined,
      getReadiness: () => undefined,
      getTimeline: () => [],
      listRoutes: () => noOpState.routes,
      listVisibleControls: () => [],
      getInteractions: () => [],
      clearInteractions: () => undefined,
      updateRoute: () => undefined,
      updatePage: () => undefined,
      clearPage: () => undefined,
      describeCurrentPage: () => ({ controls: [] }),
      destroy: () => undefined,
    };
  }

  window.__AIRBRX_AGENT__?.destroy();

  const interactionBufferSize = config.interactionBufferSize ?? DEFAULT_INTERACTION_BUFFER_SIZE;
  const timelineBufferSize = config.timelineBufferSize ?? DEFAULT_TIMELINE_BUFFER_SIZE;
  const readinessBufferSize = config.readinessBufferSize ?? DEFAULT_READINESS_BUFFER_SIZE;
  const routes = config.routes;
  const serializedRoutes = routes.map(toSerializableRoute);
  const initializedAt = new Date().toISOString();
  let route: RouteState | undefined;
  let page: AirbrxAgentPageSnapshot | undefined;
  let readiness: AirbrxAgentReadinessSnapshot | undefined;
  const interactions: AirbrxAgentInteraction[] = [];
  const routeHistory: AirbrxAgentRecordedRoute[] = [];
  const pageHistory: AirbrxAgentRecordedPage[] = [];
  const readinessHistory: AirbrxAgentReadinessSnapshot[] = [];
  const timeline: AirbrxAgentTimelineEvent[] = [];
  const runtimePerformance: AirbrxAgentRuntimePerformanceState = {
    longTaskCount: 0,
    totalLongTaskMs: 0,
    maxLongTaskMs: 0,
    interactionEventCount: 0,
    slowInteractionCount: 0,
    worstInteractionMs: 0,
    layoutShiftCount: 0,
    cumulativeLayoutShift: 0,
    maxLayoutShift: 0,
  };
  const performanceObservers: PerformanceObserver[] = [];
  let lastHoverKey = "";
  let lastHoverAtMs = 0;
  let routeUpdatedAtMs: number | undefined;
  let pageUpdatedAtMs: number | undefined;
  let lastReadinessFingerprint = "";
  let readinessTimer: number | undefined;

  const pushTimeline = (event: Omit<AirbrxAgentTimelineEvent, "timestamp" | "atMs" | "app" | "path" | "routeId" | "pageId">): void => {
    const now = Date.now();
    pushLimited(
      timeline,
      {
        timestamp: new Date(now).toISOString(),
        atMs: now,
        app: config.app,
        path: window.location.pathname,
        routeId: route?.id,
        pageId: page?.pageId,
        ...event,
      },
      timelineBufferSize,
    );
  };

  const captureReadiness = (detail = "mutation", force = false): AirbrxAgentReadinessSnapshot => {
    const controls = listVisibleControlsFromDocument();
    const snapshot = buildReadinessSnapshot(controls, route, page, routeUpdatedAtMs, pageUpdatedAtMs, runtimePerformance);
    const fingerprint = readinessFingerprint(snapshot);
    if (force || fingerprint !== lastReadinessFingerprint) {
      readiness = snapshot;
      lastReadinessFingerprint = fingerprint;
      pushLimited(readinessHistory, snapshot, readinessBufferSize);
      pushTimeline({ kind: "readiness", detail });
      updateDocumentAttributes(config.app, route, page, readiness);
      document.dispatchEvent(new CustomEvent("airbrx:agent-readiness", { detail: cloneValue(snapshot) }));
    }
    return readiness || snapshot;
  };

  const scheduleReadinessCapture = (detail = "mutation", delayMs = 120): void => {
    if (readinessTimer !== undefined) window.clearTimeout(readinessTimer);
    readinessTimer = window.setTimeout(() => {
      readinessTimer = undefined;
      captureReadiness(detail);
    }, delayMs);
  };

  const pushInteraction = (type: AirbrxAgentInteraction["type"], element: HTMLElement): void => {
    const descriptor = buildControlDescriptor(element);
    const now = Date.now();
    const interaction: AirbrxAgentInteraction = {
      timestamp: new Date(now).toISOString(),
      atMs: now,
      type,
      app: config.app,
      path: window.location.pathname,
      routeId: route?.id,
      pageId: page?.pageId,
      label: descriptor.label,
      target: descriptor.selectorHint,
      testId: descriptor.testId,
      role: descriptor.role,
      valuePreview: sanitizeValuePreview(element),
    };
    interactions.push(interaction);
    while (interactions.length > interactionBufferSize) interactions.shift();
    markUserTiming(`airbrx:interaction:${type}`);
    pushTimeline({
      kind: "interaction",
      detail: descriptor.label || descriptor.selectorHint,
      target: descriptor.selectorHint,
      interactionType: type,
    });
    scheduleReadinessCapture(`interaction:${type}`, type === "click" || type === "submit" ? 160 : 80);
  };

  const mutationObserver = new MutationObserver(() => {
    scheduleReadinessCapture("dom-mutation", 150);
  });

  const observePerformanceEntries = (
    options: { type: string; buffered?: boolean; durationThreshold?: number },
    onEntries: (entries: PerformanceEntry[]) => void,
  ): void => {
    if (typeof PerformanceObserver === "undefined") return;
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length === 0) return;
        onEntries(entries);
      });
      observer.observe(options as PerformanceObserverInit);
      performanceObservers.push(observer);
    } catch {
      // Not all runtimes support every observer entry type.
    }
  };

  observePerformanceEntries({ type: "longtask", buffered: true }, (entries) => {
    let changed = false;
    for (const entry of entries) {
      const duration = Math.round(entry.duration || 0);
      if (!Number.isFinite(duration) || duration <= 0) continue;
      runtimePerformance.longTaskCount += 1;
      runtimePerformance.totalLongTaskMs += duration;
      runtimePerformance.maxLongTaskMs = Math.max(runtimePerformance.maxLongTaskMs, duration);
      changed = true;
    }
    if (changed) scheduleReadinessCapture("performance:longtask", 40);
  });

  observePerformanceEntries({ type: "event", buffered: true, durationThreshold: 16 }, (entries) => {
    let changed = false;
    for (const entry of entries) {
      const duration = Math.round(entry.duration || 0);
      if (!Number.isFinite(duration) || duration <= 0) continue;
      runtimePerformance.interactionEventCount += 1;
      runtimePerformance.worstInteractionMs = Math.max(runtimePerformance.worstInteractionMs, duration);
      if (duration >= 120) runtimePerformance.slowInteractionCount += 1;
      changed = true;
    }
    if (changed) scheduleReadinessCapture("performance:event", 40);
  });

  observePerformanceEntries({ type: "layout-shift", buffered: true }, (entries) => {
    let changed = false;
    for (const entry of entries) {
      const shiftEntry = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean };
      if (shiftEntry.hadRecentInput) continue;
      const value = Number(shiftEntry.value || 0);
      if (!Number.isFinite(value) || value <= 0) continue;
      runtimePerformance.layoutShiftCount += 1;
      runtimePerformance.cumulativeLayoutShift += value;
      runtimePerformance.maxLayoutShift = Math.max(runtimePerformance.maxLayoutShift, value);
      changed = true;
    }
    if (changed) scheduleReadinessCapture("performance:layout-shift", 40);
  });

  const eventHandlers: Array<[keyof DocumentEventMap, EventListener]> = [
    [
      "click",
      (event: Event) => {
        const element = resolveInteractiveElement(event.target);
        if (element) pushInteraction("click", element);
      },
    ],
    [
      "focusin",
      (event: Event) => {
        const element = resolveInteractiveElement(event.target);
        if (element) pushInteraction("focus", element);
      },
    ],
    [
      "focusout",
      (event: Event) => {
        const element = resolveInteractiveElement(event.target);
        if (element) pushInteraction("blur", element);
      },
    ],
    [
      "change",
      (event: Event) => {
        const element = resolveInteractiveElement(event.target);
        if (element) pushInteraction("change", element);
      },
    ],
    [
      "submit",
      (event: Event) => {
        const element = resolveInteractiveElement(event.target) ?? (event.target instanceof HTMLElement ? event.target : null);
        if (element) pushInteraction("submit", element);
      },
    ],
    [
      "pointerover",
      (event: Event) => {
        const element = resolveInteractiveElement(event.target);
        if (!element) return;
        const key = selectorHintForElement(element);
        const now = Date.now();
        if (key === lastHoverKey && now - lastHoverAtMs < 500) return;
        lastHoverKey = key;
        lastHoverAtMs = now;
        pushInteraction("hover", element);
      },
    ],
  ];

  for (const [type, handler] of eventHandlers) {
    document.addEventListener(type, handler, true);
  }
  mutationObserver.observe(document.body, { subtree: true, childList: true, attributes: true, characterData: true });

  const api: AirbrxAgentApi = {
    getState() {
      return cloneValue({
        app: config.app,
        initializedAt,
        route,
        page,
        readiness,
        routes: serializedRoutes,
        interactions,
        routeHistory,
        pageHistory,
        readinessHistory,
        timeline,
      });
    },
    getPage() {
      return page ? cloneValue(page) : undefined;
    },
    getReadiness() {
      return readiness ? cloneValue(readiness) : undefined;
    },
    getTimeline() {
      return cloneValue(timeline);
    },
    listRoutes() {
      return cloneValue(serializedRoutes);
    },
    listVisibleControls() {
      return cloneValue(listVisibleControlsFromDocument());
    },
    getInteractions() {
      return cloneValue(interactions);
    },
    clearInteractions() {
      interactions.length = 0;
    },
    updateRoute(path: string) {
      const matched = matchAirbrxRoute(path, routes);
      route = matched
        ? {
            id: matched.id,
            title: matched.title,
            path,
            examplePath: matched.examplePath,
            navLabel: matched.navLabel,
            summary: matched.summary,
            primaryActions: matched.primaryActions,
          }
        : {
            id: "unmapped",
            title: document.title || path,
            path,
            examplePath: path,
          };
      routeUpdatedAtMs = Date.now();
      pushLimited(
        routeHistory,
        { timestamp: new Date(routeUpdatedAtMs).toISOString(), atMs: routeUpdatedAtMs, route: cloneValue(route) },
        timelineBufferSize,
      );
      markUserTiming(`airbrx:route:${route.id}`);
      updateDocumentAttributes(config.app, route, page, readiness);
      pushTimeline({ kind: "route", detail: route.id });
      document.dispatchEvent(new CustomEvent("airbrx:agent-route", { detail: cloneValue(route) }));
      captureReadiness("route-update", true);
    },
    updatePage(nextPage: AirbrxAgentPageSnapshot) {
      page = cloneValue({
        ...nextPage,
        path: nextPage.path || window.location.pathname,
      });
      pageUpdatedAtMs = Date.now();
      pushLimited(
        pageHistory,
        { timestamp: new Date(pageUpdatedAtMs).toISOString(), atMs: pageUpdatedAtMs, page: cloneValue(page) },
        timelineBufferSize,
      );
      markUserTiming(`airbrx:page:${page.pageId}`);
      updateDocumentAttributes(config.app, route, page, readiness);
      pushTimeline({ kind: "page", detail: page.pageId });
      document.dispatchEvent(new CustomEvent("airbrx:agent-page", { detail: cloneValue(page) }));
      captureReadiness("page-update", true);
    },
    clearPage(pageId?: string) {
      if (pageId && page?.pageId !== pageId) return;
      const clearedPageId = page?.pageId;
      page = undefined;
      pageUpdatedAtMs = undefined;
      updateDocumentAttributes(config.app, route, page, readiness);
      pushTimeline({ kind: "page-cleared", detail: clearedPageId });
      captureReadiness("page-cleared", true);
    },
    describeCurrentPage() {
      return {
        route: route ? cloneValue(route) : undefined,
        page: page ? cloneValue(page) : undefined,
        readiness: readiness ? cloneValue(readiness) : cloneValue(captureReadiness("describe", true)),
        controls: cloneValue(listVisibleControlsFromDocument()),
      };
    },
    destroy() {
      for (const [type, handler] of eventHandlers) {
        document.removeEventListener(type, handler, true);
      }
      mutationObserver.disconnect();
      for (const observer of performanceObservers) observer.disconnect();
      if (readinessTimer !== undefined) {
        window.clearTimeout(readinessTimer);
        readinessTimer = undefined;
      }
      if (window.__AIRBRX_AGENT__ === api) {
        delete window.__AIRBRX_AGENT__;
      }
      if (window.airbrxAgent === api) {
        delete window.airbrxAgent;
      }
      updateDocumentAttributes(config.app, undefined, undefined, undefined);
    },
  };

  window.__AIRBRX_AGENT__ = api;
  window.airbrxAgent = api;
  api.updateRoute(window.location.pathname);

  return api;
}

export function useAirbrxAgentPage(getPage: () => AirbrxAgentPageSnapshot): void {
  let lastPageId: string | undefined;
  createEffect(() => {
    if (typeof window === "undefined") return;
    const api = window.__AIRBRX_AGENT__;
    if (!api) return;
    const page = getPage();
    lastPageId = page.pageId;
    api.updatePage(page);
  });
  onCleanup(() => {
    if (typeof window === "undefined") return;
    const api = window.__AIRBRX_AGENT__;
    if (!api || !lastPageId) return;
    api.clearPage(lastPageId);
  });
}
